/**
 * Shared GraphQL error message extraction + translation.
 *
 * Extracts the first error message from Apollo Client errors (v4 `errors` or legacy `graphQLErrors`),
 * maps known backend error patterns to i18n keys, and returns a user-friendly translated string.
 *
 * Usage:
 *   catch (err) { setError(getGraphQLErrorMessage(err, t, 'Failed to create folder')); }
 *   catch (err) { toast.error(getGraphQLErrorMessage(err, t)); }
 */

/** Safely extract code and message from an unknown error (replaces `err as { code?: string }`). */
export function toErrorLike(err: unknown): { code?: string; message?: string } {
  if (err instanceof Error) {
    const code = 'code' in err && typeof err.code === 'string' ? err.code : undefined;
    return { code, message: err.message };
  }
  if (typeof err === 'object' && err !== null) {
    const obj = err as Record<string, unknown>;
    return {
      code: typeof obj.code === 'string' ? obj.code : undefined,
      message: typeof obj.message === 'string' ? obj.message : undefined,
    };
  }
  return { message: String(err) };
}

/** Known backend error patterns → i18n keys */
const ERROR_PATTERN_MAP: Array<{ pattern: RegExp; key: string }> = [
  // White-label login/signup gate (SCIO 1.0 no_acess_msg) — account isn't branding-entitled or
  // doesn't own/inherit the custom domain. Map the raw code to the friendly message.
  {
    pattern: /NO_BRANDING_ACCESS/,
    key: 'You are not a member of this organization. Please contact your account Administrator.',
  },
  { pattern: /duplicate key|duplicateFolderName/i, key: 'Duplicate folder name' },
  { pattern: /invalid path/i, key: 'Invalid folder path' },
  {
    // matches the prose ("permission denied") and the raw backend code ("PERMISSION_DENIED")
    pattern: /permission[ _]denied|forbidden/i,
    key: 'You do not have permission to perform this action',
  },
  { pattern: /not found/i, key: 'The requested resource was not found' },
  // HTTP 413 from the body-parser limit. Apollo's network-level message looks
  // like `Response not successful: Received status code 413`. Map to a clear,
  // actionable hint instead of leaking the raw status code to users.
  {
    pattern: /status code 413|payload too large|request entity too large/i,
    key: 'The upload is too large. Please use a smaller file and try again.',
  },
  // Web Scripting (scriptingAbility) — BE throws raw English from asset.service.ts + asset.utils.ts
  { pattern: /master password is incorrect/i, key: 'Master Password is incorrect.' },
  { pattern: /invalid recorded script/i, key: 'Invalid Recorded Script' },
  // Schedule item duration limits — BE NotAcceptableException from schedule.service.ts
  {
    pattern: /schedule items must be 24h or less/i,
    key: 'To prevent potential conflict, overlaps, schedule items must be 24h or less',
  },
  {
    pattern: /schedule items must be 28 days or less/i,
    key: 'To prevent potential conflict, overlaps, schedule items must be 28 days or less',
  },
];

/**
 * Extract user-friendly error message from an Apollo GraphQL error.
 *
 * @param error - The caught error (Apollo ApolloError, CombinedGraphQLErrors, or plain Error)
 * @param t - i18next `t()` function for translation. If provided, known patterns are translated.
 * @param fallbackKey - i18n key to use when no message can be extracted
 * @returns Translated or raw error message string
 */
export function getGraphQLErrorMessage(
  error: unknown,
  t?: (key: string) => string,
  fallbackKey?: string,
): string {
  const err = error as {
    graphQLErrors?: Array<{ message?: string }>;
    errors?: Array<{ message?: string }>;
    message?: string;
  };

  const rawMsg =
    err?.graphQLErrors?.[0]?.message ?? err?.errors?.[0]?.message ?? err?.message ?? '';

  if (t) {
    for (const { pattern, key } of ERROR_PATTERN_MAP) {
      if (pattern.test(rawMsg)) return t(key);
    }
  }

  return rawMsg || (t && fallbackKey ? t(fallbackKey) : 'An error occurred');
}

/**
 * Extract the custom error code from a GraphQL error.
 *
 * NestJS ForbiddenException({ message, code }) serializes as:
 *   - extensions.exception.response.code (debug/dev mode)
 *   - extensions.code (production GraphQL error code, e.g. 'FORBIDDEN')
 *   - message (fallback: the error message itself)
 *
 * Checks nested NestJS exception response first, then recognizes the CAPTCHA
 * challenge message wrapped by FORBIDDEN, then checks extensions.code and message.
 */
export function getGraphQLErrorCode(error: unknown): string | undefined {
  const err = error as {
    graphQLErrors?: Array<{
      message?: string;
      extensions?: Record<string, unknown>;
    }>;
    errors?: Array<{
      message?: string;
      extensions?: Record<string, unknown>;
    }>;
    message?: string;
  };

  const firstError = err?.errors?.[0] ?? err?.graphQLErrors?.[0];
  if (!firstError) return undefined;

  // NestJS nested exception response (debug mode)
  const ext = firstError.extensions;
  const nestedResponse = (ext?.exception as Record<string, unknown> | undefined)?.response as
    | Record<string, unknown>
    | undefined;
  if (typeof nestedResponse?.code === 'string') return nestedResponse.code;

  // NestJS may wrap the CAPTCHA challenge in a generic FORBIDDEN code without
  // a nested application code. Preserve the challenge so callers can show v2.
  if (ext?.code === 'FORBIDDEN' && firstError.message === 'RECAPTCHA_CHALLENGE_REQUIRED') {
    return firstError.message;
  }

  // Top-level GraphQL error code
  if (typeof ext?.code === 'string') return ext.code;

  // Message-based fallback
  return firstError.message ?? undefined;
}

/**
 * Extract specific error reason from GraphQL error extensions.
 * Backend throws ForbiddenException("PERMISSION_DENIED", "SPECIFIC_CODE")
 * → extensions.exception.response.error = "SPECIFIC_CODE"
 * The returned code can be used directly as an i18n translation key.
 */
export function getMoveErrorReason(err: unknown): string | null {
  const gqlError = err as {
    // Apollo Client 4 uses `errors`
    errors?: Array<{ message?: string; extensions?: Record<string, unknown> }>;
    // Legacy Apollo Client uses `graphQLErrors`
    graphQLErrors?: Array<{ message?: string; extensions?: Record<string, unknown> }>;
  };
  const firstError = gqlError?.errors?.[0] ?? gqlError?.graphQLErrors?.[0];
  if (!firstError) return null;

  // Two-arg pattern: specific reason in extensions.exception.response.error
  const ext = firstError.extensions;
  const response = (ext?.exception as Record<string, unknown> | undefined)?.response as
    | Record<string, unknown>
    | undefined;
  const reason = response?.error;
  // Filter out generic HTTP status texts (e.g. "Not Acceptable", "Forbidden") —
  // only return app-specific error codes like "DEVICE_FOLDER_NOT_EMPTY".
  // HTTP status texts are title-cased words without underscores; app codes use UPPER_SNAKE_CASE.
  if (typeof reason === 'string' && /^[A-Z][A-Z0-9_]*(_[A-Z0-9]+)+$/.test(reason)) return reason;

  // Single-arg pattern: error code is the message itself (e.g. "duplicateFolderName").
  // Map known camelCase codes to existing i18n keys.
  const MESSAGE_CODE_MAP: Record<string, string> = {
    duplicateFolderName: 'Duplicate folder name',
    duplicateFolderTeamName: 'Duplicate folder name shared from other team',
  };
  if (typeof firstError.message === 'string' && firstError.message in MESSAGE_CODE_MAP) {
    return MESSAGE_CODE_MAP[firstError.message];
  }

  return null;
}

/**
 * Extract the HTTP status code from a GraphQL error.
 *
 * NestJS HttpExceptions surface their status in several places depending on mode:
 *   - extensions.status (Apollo formatted error)
 *   - extensions.exception.status / extensions.exception.response.statusCode (debug mode)
 *   - extensions.originalError.statusCode
 *
 * Use this to distinguish error classes reliably instead of matching message text —
 * e.g. a genuine overlap is a ConflictException (409) while a duration-limit rejection is a
 * NotAcceptableException (406), and both messages contain the words "conflict"/"overlaps".
 */
export function getGraphQLErrorStatus(error: unknown): number | undefined {
  const err = error as {
    errors?: Array<{ extensions?: Record<string, unknown> }>;
    graphQLErrors?: Array<{ extensions?: Record<string, unknown> }>;
  };
  const ext = (err?.errors?.[0] ?? err?.graphQLErrors?.[0])?.extensions;
  if (!ext) return undefined;

  const exception = ext.exception as Record<string, unknown> | undefined;
  const exceptionResponse = exception?.response as Record<string, unknown> | undefined;
  const originalError = ext.originalError as Record<string, unknown> | undefined;

  const candidates = [
    ext.status,
    exception?.status,
    exceptionResponse?.statusCode,
    originalError?.statusCode,
  ];
  const status = candidates.find((v) => typeof v === 'number');
  return typeof status === 'number' ? status : undefined;
}
